// EveningRecapModal.jsx
//
// End-of-day "did you finish?" sheet. Opens automatically on the
// dashboard the first time the user lands in the app after their
// configured recap time (default 9:30 PM local). Two paths:
//
//   ▸ Type  — checklist of today's habits, tap to toggle.
//   ▸ Speak — record what they did and didn't do; Whisper transcribes,
//             Claude maps each habit to "done", "notDone", or "skip",
//             the checklist updates accordingly, user can adjust before
//             tapping Save.
//
// All status changes commit through the standard toggleCheckin mutation,
// so the dashboard grid + analytics + streaks all stay in lockstep with
// whatever the user records here.

import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.jsx';
import { CheckIcon } from './CheckIcon.jsx';
import { Waveform } from './Waveform.jsx';
import { ProcessingOrb } from './ProcessingOrb.jsx';
import { transcribeAudio, callLLM } from '../lib/llm.js';
import { AIConsentDeclinedError } from '../lib/aiConsent.js';
import { sfx } from '../lib/sfx.js';
import { resolveAccentColor } from '../lib/constants.js';
import { useTheme } from '../lib/themeStore.js';

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
];
function pickMime() {
  if (!window.MediaRecorder?.isTypeSupported) return '';
  for (const t of MIME_CANDIDATES) {
    try { if (MediaRecorder.isTypeSupported(t)) return t; } catch {}
  }
  return '';
}

const STATUS = { DONE: 'done', NOT_DONE: 'notDone', SKIP: 'skip' };

// Build a map of { habitId: 'done' | 'notDone' | 'skip' } prepopulated
// from the user's existing check-ins so unticked habits start as
// "skip" (we don't presume anything until the user says/taps).
function initialStatusMap(habits, doneSet, isoDate) {
  const out = {};
  for (const h of habits) {
    out[h.id] = doneSet.has(`${h.id}|${isoDate}`) ? STATUS.DONE : STATUS.SKIP;
  }
  return out;
}

export function EveningRecapModal({
  open,
  habits,             // today's scheduled habits
  doneSet,            // Set of "habitId|isoDate" strings, current state
  isoDate,            // today's iso date
  onSave,             // (statusMap) => Promise — caller commits checkins
  onClose,
  saving = false,
  errorMsg = '',
}) {
  const theme = useTheme();
  const [mode, setMode] = useState('idle');     // idle | type | speak
  const [phase, setPhase] = useState('idle');   // idle | init | recording | transcribing | parsing | error
  const [elapsed, setElapsed] = useState(0);
  const [errMsg, setErrMsg] = useState('');
  const [analyser, setAnalyser] = useState(null);
  const [statusMap, setStatusMap] = useState(() => initialStatusMap(habits, doneSet, isoDate));

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const startedAtRef = useRef(0);
  const cancelledRef = useRef(false);
  const finishingRef = useRef(false);
  const audioCtxRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const analyserNodeRef = useRef(null);

  // Reset everything on close.
  useEffect(() => {
    if (!open) {
      setMode('idle');
      setPhase('idle');
      setElapsed(0);
      setErrMsg('');
      setAnalyser(null);
    } else {
      // On open, refresh the prepopulated map from latest props in case
      // the user toggled something manually right before the recap fired.
      setStatusMap(initialStatusMap(habits, doneSet, isoDate));
    }
  }, [open, habits, doneSet, isoDate]);

  const teardown = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    try { recorderRef.current?.state === 'recording' && recorderRef.current.stop(); } catch {}
    try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch {}
    recorderRef.current = null;
    streamRef.current = null;
    try { sourceNodeRef.current?.disconnect(); } catch {}
    try { analyserNodeRef.current?.disconnect(); } catch {}
    try { audioCtxRef.current?.close?.(); } catch {}
    sourceNodeRef.current = null;
    analyserNodeRef.current = null;
    audioCtxRef.current = null;
    setAnalyser(null);
  };
  useEffect(() => () => { cancelledRef.current = true; teardown(); }, []);

  const toggleHabitStatus = (id) => {
    setStatusMap(prev => {
      const cur = prev[id];
      // Three-state cycle: done → notDone → skip → done
      const next = cur === STATUS.DONE ? STATUS.NOT_DONE
                : cur === STATUS.NOT_DONE ? STATUS.SKIP
                : STATUS.DONE;
      sfx.tick && sfx.tick();
      return { ...prev, [id]: next };
    });
  };

  // ── Recording lifecycle ──────────────────────────────────────────
  const startRecording = async () => {
    setErrMsg('');
    setPhase('init');
    cancelledRef.current = false;
    finishingRef.current = false;
    setElapsed(0);
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        throw new Error('Microphone API not available on this device.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (cancelledRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = stream;

      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          try { ctx.resume?.().catch(() => {}); } catch {}
          const source = ctx.createMediaStreamSource(stream);
          const node = ctx.createAnalyser();
          node.fftSize = 256;
          node.smoothingTimeConstant = 0.75;
          node.minDecibels = -85;
          node.maxDecibels = -20;
          source.connect(node);
          audioCtxRef.current = ctx;
          sourceNodeRef.current = source;
          analyserNodeRef.current = node;
          setAnalyser(node);
        }
      } catch { /* equalizer is decorative */ }

      const mimeType = pickMime();
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch {}
        streamRef.current = null;
        if (cancelledRef.current) return;

        const type = chunksRef.current[0]?.type || rec.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) {
          setErrMsg("I didn't catch any audio. Try again?");
          setPhase('error');
          return;
        }

        setPhase('transcribing');
        sfx.startTranscribing && sfx.startTranscribing();
        let transcript = '';
        try {
          transcript = await transcribeAudio(blob);
          if (cancelledRef.current) return;
          if (!transcript) {
            setErrMsg("I couldn't make out any speech. Try again?");
            setPhase('error');
            return;
          }
        } catch (err) {
          if (cancelledRef.current) return;
          if (err instanceof AIConsentDeclinedError) { setPhase('idle'); return; }
          setErrMsg(err?.message || 'Transcription failed. Try again?');
          setPhase('error');
          return;
        } finally {
          sfx.stopTranscribing && sfx.stopTranscribing();
        }

        // Hand the transcript to Claude to map onto today's habit list.
        setPhase('parsing');
        sfx.startSaving && sfx.startSaving();
        try {
          const map = await parseRecap(transcript, habits);
          if (cancelledRef.current) return;
          setStatusMap(prev => {
            const next = { ...prev };
            for (const [id, status] of Object.entries(map)) {
              if (status === STATUS.DONE || status === STATUS.NOT_DONE) {
                next[id] = status;
              }
            }
            return next;
          });
          setPhase('idle');
          setMode('type'); // drop into the editable checklist
          sfx.ready && sfx.ready();
        } catch (err) {
          if (cancelledRef.current) return;
          if (err instanceof AIConsentDeclinedError) { setPhase('idle'); return; }
          setErrMsg(err?.message || "Couldn't parse what you said. Tap habits below to mark them.");
          setPhase('idle');
          setMode('type');
        } finally {
          sfx.stopSaving && sfx.stopSaving();
        }
      };

      rec.start();
      startedAtRef.current = Date.now();
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
      setPhase('recording');
      sfx.recordStart && sfx.recordStart();
    } catch (err) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Microphone access was denied. Enable it in Settings to use voice input.'
        : (err?.message || 'Could not access the microphone.');
      setErrMsg(msg);
      setPhase('error');
    }
  };

  const stopAndTranscribe = () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    sfx.toast && sfx.toast();
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    try { recorderRef.current?.stop(); } catch {}
  };

  const cancelVoice = () => {
    cancelledRef.current = true;
    sfx.sheetClose && sfx.sheetClose();
    teardown();
    setPhase('idle');
    setMode('idle');
  };

  const retry = () => {
    setErrMsg('');
    cancelledRef.current = false;
    finishingRef.current = false;
    setElapsed(0);
    teardown();
    startRecording();
  };

  if (!open) return null;

  const showVoiceSurface = phase === 'init' || phase === 'recording' || phase === 'transcribing' || phase === 'parsing' || phase === 'error';
  const showChecklist = (mode === 'type' || (mode === 'speak' && phase === 'idle')) && !showVoiceSurface;

  const mm = String(Math.floor(elapsed / 60)).padStart(1, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  const doneCount = Object.values(statusMap).filter(s => s === STATUS.DONE).length;
  const notDoneCount = Object.values(statusMap).filter(s => s === STATUS.NOT_DONE).length;

  return (
    <>
      <div
        onClick={() => (saving ? null : onClose())}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
          zIndex: 200, animation: 'fadeIn 140ms ease-out',
        }}
      />
      {/* Flex-centered wrapper — separates centering from animation so
          sheetIn doesn't briefly land at top-left. */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 201,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
          pointerEvents: 'none',
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          style={{
            width: '100%',
            maxWidth: 480,
            maxHeight: '85vh',
            overflowY: 'auto',
            background: 'rgba(20,24,32,0.98)',
            border: '1px solid var(--stroke-1)',
            borderRadius: 22,
            padding: '22px 22px 18px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
            animation: 'sheetIn 220ms cubic-bezier(0.22,1,0.36,1)',
            pointerEvents: 'auto',
          }}
        >
          <div style={{
            fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 19,
            color: 'var(--fg-1)', letterSpacing: '-0.01em',
          }}>
            Did you finish today?
          </div>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-3)',
            lineHeight: 1.5, marginTop: 6,
          }}>
            Speak what you did, or tap each habit. {doneCount > 0 || notDoneCount > 0 ? `${doneCount} done, ${notDoneCount} skipped.` : `${habits.length} habit${habits.length === 1 ? '' : 's'} for today.`}
          </div>

          {/* Mode picker — Speak primary, Type secondary, identical to Goals. */}
          {mode === 'idle' && !saving && !showVoiceSurface && (
            <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'center' }}>
              <button
                onClick={() => { sfx.nav && sfx.nav(); setMode('speak'); startRecording(); }}
                className="btn btn-primary"
                style={{
                  padding: '11px 22px', borderRadius: 999,
                  gap: 8, display: 'inline-flex', alignItems: 'center',
                }}
              >
                <Icon name="mic" size={14} color="#fff"/> Speak
              </button>
              <button
                onClick={() => { sfx.nav && sfx.nav(); setMode('type'); }}
                className="btn"
                style={{
                  padding: '11px 22px', borderRadius: 999,
                  gap: 8, display: 'inline-flex', alignItems: 'center',
                  background: 'rgba(255,255,255,0.06)', color: 'var(--fg-1)',
                  border: '1px solid var(--stroke-2)',
                }}
              >
                <Icon name="clipboard" size={14}/> Type
              </button>
            </div>
          )}

          {/* Voice surface — equalizer / orb / error */}
          {showVoiceSurface && (
            <div style={{
              marginTop: 22,
              padding: '20px 18px 16px',
              borderRadius: 18,
              background: 'rgba(45,156,255,0.06)',
              border: '1px solid rgba(45,156,255,0.22)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
            }}>
              {phase === 'transcribing' && (
                <ProcessingOrb size={64} label="Transcribing" sublabel="Turning your voice into text."/>
              )}
              {phase === 'parsing' && (
                <ProcessingOrb size={64} label="Mapping to your habits" sublabel="Checking which ones you finished."/>
              )}
              {(phase === 'init' || phase === 'recording') && (
                <>
                  <div style={{ width: '100%', maxWidth: 260, display: 'flex', justifyContent: 'center' }}>
                    <Waveform analyser={analyser}/>
                  </div>
                  <div style={{ textAlign: 'center', minHeight: 32 }}>
                    {phase === 'init' && <span style={mono}>Waking up the mic…</span>}
                    {phase === 'recording' && (
                      <div>
                        <div style={mono}>Listening</div>
                        <div style={{ ...mono, fontSize: 22, color: 'var(--fg-1)', marginTop: 4 }}>{mm}:{ss}</div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={stopAndTranscribe}
                    style={{
                      padding: '11px 26px', borderRadius: 999,
                      background: 'linear-gradient(180deg, #47A6FF, #2D7DE0)',
                      border: '1px solid rgba(71,166,255,0.55)',
                      color: '#fff',
                      fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5,
                      cursor: 'pointer',
                      boxShadow: '0 8px 22px rgba(45,125,224,0.4)',
                    }}
                  >Stop &amp; review</button>
                  <button
                    onClick={cancelVoice}
                    style={{ padding: '6px 14px', borderRadius: 8, background: 'transparent', border: 'none', color: 'var(--fg-4)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12 }}
                  >Cancel</button>
                </>
              )}
              {phase === 'error' && (
                <>
                  <span style={{ color: '#FF8699', textAlign: 'center' }}>{errMsg}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={cancelVoice} style={ghostBtn}>Cancel</button>
                    <button onClick={retry} style={{ padding: '9px 18px', borderRadius: 999, background: 'linear-gradient(180deg, #47A6FF, #2D7DE0)', border: '1px solid rgba(71,166,255,0.55)', color: '#fff', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>Try again</button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Checklist — visible after voice parse OR if user picked Type. */}
          {showChecklist && (
            <div style={{ marginTop: 18 }}>
              <div style={{
                borderRadius: 14,
                border: '1px solid var(--stroke-1)',
                background: 'rgba(255,255,255,0.03)',
                overflow: 'hidden',
              }}>
                {habits.map((h, i) => {
                  const status = statusMap[h.id] || STATUS.SKIP;
                  const accent = resolveAccentColor(h.accent, theme);
                  return (
                    <button
                      key={h.id}
                      onClick={() => toggleHabitStatus(h.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        width: '100%', padding: '12px 14px',
                        background: 'transparent', border: 'none',
                        borderBottom: i < habits.length - 1 ? '1px solid var(--stroke-1)' : 'none',
                        color: 'var(--fg-1)', cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <RecapStatusDot status={status} accent={accent}/>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--fg-1)' }}>
                          {h.name}
                        </div>
                        <div style={{ fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 2 }}>
                          {status === STATUS.DONE ? 'Done' : status === STATUS.NOT_DONE ? 'Skipped today' : 'Tap to mark'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  onClick={() => { sfx.nav && sfx.nav(); setMode('speak'); startRecording(); }}
                  style={ghostBtn}
                >
                  <Icon name="mic" size={13}/>
                  Add by voice
                </button>
              </div>

              {(errorMsg || errMsg) && (
                <div style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 12,
                  background: 'rgba(255,72,72,0.10)',
                  border: '1px solid rgba(255,72,72,0.3)',
                  color: '#FFB4B4',
                  fontFamily: 'var(--font-body)', fontSize: 12,
                }}>
                  {errorMsg || errMsg}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          {!showVoiceSurface && (
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button
                onClick={() => { sfx.sheetClose && sfx.sheetClose(); onClose(); }}
                disabled={saving}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid var(--stroke-1)',
                  color: 'var(--fg-2)',
                  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13.5,
                  cursor: saving ? 'default' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Skip for tonight'}
              </button>
              <button
                onClick={() => onSave(statusMap)}
                disabled={saving || mode === 'idle'}
                style={{
                  flex: 1.4, padding: '12px 16px', borderRadius: 999,
                  background: (saving || mode === 'idle') ? 'rgba(255,255,255,0.06)' : 'linear-gradient(180deg, #47A6FF, #2D7DE0)',
                  border: (saving || mode === 'idle') ? '1px solid var(--stroke-1)' : '1px solid rgba(71,166,255,0.55)',
                  color: (saving || mode === 'idle') ? 'var(--fg-3)' : '#fff',
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5,
                  cursor: (saving || mode === 'idle') ? 'default' : 'pointer',
                  boxShadow: (saving || mode === 'idle') ? 'none' : '0 8px 22px rgba(45,125,224,0.4)',
                }}
              >
                {saving ? 'Saving…' : 'Save my recap'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function RecapStatusDot({ status, accent }) {
  if (status === STATUS.DONE) {
    return (
      <div style={{
        width: 26, height: 26, borderRadius: '50%',
        background: accent,
        display: 'grid', placeItems: 'center',
        boxShadow: `0 0 10px ${accent}55`,
        flexShrink: 0,
      }}>
        <CheckIcon size={14} color="#fff" stroke={2.6}/>
      </div>
    );
  }
  if (status === STATUS.NOT_DONE) {
    return (
      <div style={{
        width: 26, height: 26, borderRadius: '50%',
        background: 'rgba(255,72,72,0.14)',
        border: '1.5px solid rgba(255,72,72,0.5)',
        display: 'grid', placeItems: 'center',
        flexShrink: 0,
        color: '#FF8699',
        fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 14, lineHeight: 1,
      }}>×</div>
    );
  }
  return (
    <div style={{
      width: 26, height: 26, borderRadius: '50%',
      border: '1.5px dashed var(--stroke-3)',
      flexShrink: 0,
    }}/>
  );
}

// ── Claude prompt ─────────────────────────────────────────────────────
//
// We feed Claude the user's transcript plus a flat list of {id, name}
// for each habit. Claude returns a JSON object mapping id → status. We
// keep the prompt strict so the parser stays cheap and predictable.
async function parseRecap(transcript, habits) {
  if (!transcript || !habits.length) return {};
  const habitList = habits.map(h => `- ${h.id} :: ${h.name}`).join('\n');
  const system = [
    "You are a parser for a habit-tracking app. The user describes which habits they did or didn't complete today.",
    "Map each habit to one of three statuses based on the user's words:",
    '- "done"     — clearly completed',
    '- "notDone"  — clearly skipped or said no to',
    '- "skip"     — not mentioned, ambiguous, or unclear',
    "",
    "Output ONLY a JSON object whose keys are habit ids and whose values are one of those three strings. No prose, no code fences.",
    "Do NOT mark a habit done if the user only mentioned a related word in passing — require positive language (\"I did X\", \"finished\", \"hit\", \"got it\", etc.). Use \"skip\" liberally when in doubt.",
    "If the user said something like \"I did everything\", mark every habit \"done\". If \"I did nothing\", mark every habit \"notDone\".",
  ].join('\n');
  const user = `Habits today:\n${habitList}\n\nUser said: ${transcript}\n\nOutput a JSON object now.`;
  const text = await callLLM({ system, messages: [{ role: 'user', content: user }] });
  const cleaned = (text || '').trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```\s*$/, '')
    .trim();
  try {
    const parsed = JSON.parse(cleaned);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const out = {};
      for (const h of habits) {
        const v = parsed[h.id];
        if (v === STATUS.DONE || v === STATUS.NOT_DONE || v === STATUS.SKIP) out[h.id] = v;
      }
      return out;
    }
  } catch { /* fall through */ }
  return {};
}

const mono = {
  fontFamily: 'ui-monospace, SF Mono, Menlo, Consolas, monospace',
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--accent-hi)',
};
const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 12px', borderRadius: 999,
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--stroke-1)',
  color: 'var(--fg-2)',
  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12,
  cursor: 'pointer',
};
